<script lang="ts" setup>
import dayjs from 'dayjs'
import LinePlayCircle from '@docue/docue-ui-v2/icons/SolidPlayCircle.vue'

const props = defineProps<{
  videoIds: string[]
}>()

const emit = defineEmits<{
  videoClicked: [id: string]
}>()

interface VideoItem {
  id: string
  title: string
  durationText: string
  thumbnail: {
    url: string
    width: number
    height: number
  }
}

const { data: videoData, isFetching } = useQueryYoutubeVideos.byIds({
  ids: props.videoIds,
  useQueryOptions: {
    enabled: () => !!props.videoIds.length,
  },
})

const isFetchingDelayed = refDelayed(isFetching, false)

const videoItems = computed<VideoItem[]>(() =>
  (videoData.value?.items || []).map(({ id, snippet, contentDetails }) => ({
    id,
    title: snippet.title,
    thumbnail: snippet.thumbnails.high,
    durationText: dayjs.duration(contentDetails.duration).format('HH:mm:ss').replace(/^00:/, '').replace(/^0/, ''),
  })))
</script>

<template>
  <div
    v-if="isFetchingDelayed"
    class="flex h-[68px] items-center pl-3"
  >
    <DTLoaderDots />
  </div>

  <ul
    v-else
    class="flex flex-col"
  >
    <li
      v-for="{ id, title, durationText, thumbnail } in videoItems"
      :key="id"
      class="group relative flex items-center py-3 pl-3"
    >
      <div class="relative shrink-0">
        <img
          v-if="thumbnail"
          class="h-11 w-16 rounded-lg object-cover group-hover:drop-shadow-lg"
          :src="thumbnail.url"
          :width="thumbnail.width"
          :height="thumbnail.height"
          loading="lazy"
        >
        <div
          class="absolute inset-0 rounded-lg bg-black/5 group-hover:bg-transparent"
        />
        <LinePlayCircle
          class="
              absolute
              left-1/2
              top-1/2
              size-5
              -translate-x-1/2
              -translate-y-1/2
              text-white
              opacity-90
              "
        />
      </div>
      <div class="ml-4 flex flex-col">
        <a
          :href="`https://www.youtube.com/watch?v=${id}`"
          rel="noopener noreferrer"
          target="_blank"
          class="leading-tight before:absolute before:inset-0 hover:no-underline"
          @click.prevent="emit('videoClicked', id)"
        >
          <span class="text-sm text-gray-800">
            {{ title }}
          </span>
        </a>
        <span class="text-sm text-gray-400">
          {{ durationText }}
        </span>
      </div>
    </li>
  </ul>
</template>
